/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {useEffect, useMemo, useRef, useState} from 'react'
import '../../../camera/CameraPerformance.css'
import {BottomButtonsWrapper} from '../../../camera/SensorCamera/ItemDetails/DetectionCapability/styled';
import {useMsal} from '@azure/msal-react';
import ArgosButton from "../../../../components/Buttons/ArgosButton";
import {useAppSelector} from "../../../../../redux/hooks";
import ArgosTextField from "../../../../components/Form/TextField/ArgosTextField";
import ArgosFlexbox from "../../../../components/Containers/ArgosFlexbox";
import {selectGlobalDialogState} from '../../../../../redux/ducks/GlobalDialog/slice';
import {useHasWriteAccess} from "../../../../../redux/ducks/AccessControl/accessControlHelpers";

import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
} from '@mui/material';
import {usePostRequest} from "../../../../../api/helper";
import RightPanel from './CollapsibleTabs/RightPanel';
import ObjectProperties from './CollapsibleTabs/ObjectProperties';
import LidarProperties from './CollapsibleTabs/LidarProperties';
import ArgosCollapseComponent from '../../../../components/CollapseComponent/ArgosCollapseComponent';
import {theme} from '../../../../../constants/theme';
import {getLidarPlotEndpoint} from '../../constants';
import { toast } from 'react-hot-toast';
import {set} from "lodash";
import VAR_COMMON_CONSTANTS from "../../../../../constants/globalVariables";
import { COLUMN_NAMES } from '../constants';
import {MAP_OBJECT_PROFILE_NAME_TO_KEY} from "../../../radar/SensorRadar/ItemDetails/constants";
import {
  groupObjectsByTypeNameVersionProfile
} from "../../../radar/SensorRadar/ItemDetails/RadarPerformance/CollapsibleTabs/ObjectProperties";
import {addObjectIdToBaselineData, getRcs} from "../../../radar/SensorRadar/ItemDetails/RadarPerformance/Content";
import {getPmoSizes} from "../../../camera/SensorCamera/ItemDetails/DetectionCapability/Content";


const Content = (props: any) => {

  const buildPlotData = (_data: any) => {
    const data: any = {x: _data?.x, y: _data?.y}
    const columns = {xLabel: COLUMN_NAMES.AZIMUTH_ANGLE, yLabel: COLUMN_NAMES.DISTANCE}
    const returnObj = {data, columns}
    return returnObj
  }

  const hasWriteAccess = useHasWriteAccess()
  const [baselineData, setBaselineData] = useState(props.baselineData)
  const [plotData, setPlotData] = useState({})
  const [fovAndBoresightLidarValues, setFovAndBoresightLidarValues] = useState<any>({});
  const [objectData, setObjectData] = useState(props.objectData);
  const [objectDataMap, setObjectDataMap] = useState(props.objectDataMap);
  const [configName, setConfigName] = useState("");
  const [openSavePopup, setOpenSavePopup] = useState(false);
  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [isLoadingPlot, setIsLoadingPlot] = useState(false);
  const gmid = props.gmid
  const isDialogOpen = useAppSelector(selectGlobalDialogState).isOpen
  const [isError, setIsError] = useState(false);
  const [componentDataMap, setComponentDataMap] = useState<any>({});
  const [temperatureArray, setTemperatureArray] = useState<number[]>([]);
  const [sensorLidarData, setSensorLidarData] = useState<any>(props?.sensorLidarData)
  useEffect(() => {
    setConfigName(baselineData['configName'])
  }, [baselineData?.['configName']]);

  useEffect(() => {
    setBaselineData(props.baselineData);
    setObjectData(props.objectData);
    setObjectDataMap(props.objectDataMap);
  }, [props])

  useEffect(() => {
    handleUpdatePlot(props.baselineData)
  }, [props.configIndex])


  const [newTempRelatedDataList, setTempRelatedDataList] = useState<any>();
  useEffect(() => {
    setTempRelatedDataList(null)
  }, [props.tempToObjMap]);
  function handleTempChange(newTempValue: string ) {
    const _newTempRelatedDataList: any =  props?.tempToObjMap[newTempValue as unknown as number]
    const newObject = {
      ...baselineData,
      "tempRelatedDataList": _newTempRelatedDataList,
      'temperature': newTempValue,
      "selectedObject" : _newTempRelatedDataList?.objectName,
      "selectedPointsFrames" : _newTempRelatedDataList?.pointsFrames
    }
    setSensorLidarData({
      ...sensorLidarData,
    })
    setTempRelatedDataList(_newTempRelatedDataList)
    setBaselineData(newObject)
  }

  const getObjectIdBasedName = () => {
    const objectId = objectData.filter((objectElement: any) => objectElement.name === baselineData?.selectedObject)[0]?.id
    return objectId
  }
  const url = getLidarPlotEndpoint
  const getLidarPlotHandle = usePostRequest(url)
  async function handleUpdatePlot(baselineData: any) {
    setIsLoadingPlot(true)
    // if(baselineData?.selectedObject == "" || baselineData?.selectedObject == undefined){
    //   toast.error("Please select an Object to update plot")
    //   setIsLoadingPlot(false)
    //   return
    // }
    let plotDataKey: string = getPlotDataName(baselineData.temperature)

    const sensorId = sensorLidarData?.id;

    let newPlotData = {
      'objectId': getObjectIdBasedName(),
      orientationWidth: baselineData.orientationWidth,
      orientationHeight: baselineData.orientationHeight,
      pointsOnTargetWidth: baselineData.pointsOnTargetWidth,
      pointsOnTargetHeight: baselineData.pointsOnTargetHeight,
      'instrumentationRange': baselineData["maxInstrumentedRange"],
      'instrumentationAngle': baselineData["azimuthFOV"],
      'instrumentationElevation': baselineData["elevationFOV"],
      "plotData": props.sensorLidarData.plotData[plotDataKey]?.data,
      sensorId
    }
    getLidarPlotHandle(newPlotData).then((responseJson) => {
      setIsError(false)
      const fovAndBoresightLidarObject = {
        'azimuth_fov': responseJson["azimuth_fov"],
        'elevation_fov': responseJson["elevation_fov"],
        'max_range': responseJson["max_range"]
      }
      const data = {
        'x': responseJson['x'],
        'y': responseJson['y']
      }
      setPlotData(buildPlotData(data))
      setFovAndBoresightLidarValues(fovAndBoresightLidarObject)
      setIsLoadingPlot(false);
    }).catch(err => {
      toast.error("Error while updating plot. Please check your values and try again")
      setIsError(true)
      setIsLoadingPlot(false)
    })
  }

  function handleReset() {
    setBaselineData(props.baselineData)
  }

  function getPlotDataName(_tempValue: string) {
    const sensorData = props.sensorLidarData.plotData
    let detectionDataKey: any = ""
    Object.keys(sensorData)?.forEach((key:string) => {
      if(key != "instrumentationLimits"){
        const detectiondata = sensorData[key].data
        if( (key === 'Detection_Range_1' || key === 'Detection_Range_2') && props.configIndex != 0){
          return
        }
        const tempValue = Object.keys(detectiondata).filter((keys: string) => keys.includes("temperature") &&  keys.includes("value"))[0]
        if(detectiondata[tempValue] == _tempValue){
          detectionDataKey = key
        }
      }
      
    })
    return detectionDataKey
  }

  function onSaveConfiguration() {
    props.onSaveConfiguration(null, baselineData, configName, gmid)
  }

  function onOverrideConfiguration() {
    props.onSaveConfiguration(props.configIndex - 1, baselineData, configName, gmid)
  }

  const handleClose = () => {
    setOpenSavePopup(false);
    setOpenDeletePopup(false);
  };

  const handleClickSaveOpen = () => {
    setOpenSavePopup(true);
  };

  function handleSaveConfigName(e: any) {
    setConfigName(e.target.value)
  }

  function onDeleteConfiguration() {
    props.onDeleteConfiguration(props.baselineData.configName, props.baselineData.owner)
  }


  function setIsPublicConfig(_isPublicConfig: any) {
    const newObject = {
      ...baselineData,
      'isPublicConfig': _isPublicConfig
    }
    setBaselineData(newObject);
  }

  useEffect(() => {
    createCollapsibleTabs()
  }, [props, baselineData, objectData, objectDataMap, plotData, openSavePopup, configName, newTempRelatedDataList])

  const setSelectedPointsFrames = (newPointsFrames: string) => {
    setBaselineData({
        ...baselineData,
        "selectedPointsFrames": newPointsFrames
      }
    )
  }

  function getBaselineObject() {
    const baselineObjects = objectData.filter((item: any) => (item.type == "Baseline"))
    const latestVersion = baselineObjects.reduce(function (prev: any, current: any) {
      return (prev.version > current.version) ? prev : current
    })

    const baselineObject = {
      "version": latestVersion.version,
      "id": latestVersion.id,
      "links": latestVersion.links,
      "pointsOnObject": latestVersion.pointsOnObject,
      "pixelsAcrossShortDimension": latestVersion.pixelsAcrossShortDimension,
      "rcs": latestVersion.rcs,
      "heightVertical": latestVersion.heightVertical,
      "widthHorizontal": latestVersion.widthHorizontal,
      "type": "Baseline",
      "name": latestVersion.name,
      "globalId": latestVersion.globalId,
      objectVariant: latestVersion.objectVariant,
    }
    return baselineObject
  }

  function getLatestVersion(category: string, name: string) {
    let allVersions;
    if (category == "Sign") {
      allVersions = objectData.filter((item: any) => (item.type == category && item.signName == name))
    } else {
      allVersions = objectData.filter((item: any) => (item.type == category && item.objectName == name))
    }
    const latestVersion = allVersions.reduce(function (prev: any, current: any) {
      return (prev.latestVersion > current.latestVersion) ? prev : current
    })
    return latestVersion
  }

  function handleObjectCategoryChange(e: any) {
    // this just constructs the baseline object from sensor and rwo. It is being pulled from database
    const baselineObject = getBaselineObject()
    let newData = {}
    let isBaseline: boolean = e.target.value == "Baseline"
    if (e.target.value == "Baseline") {
      newData = {
        ...baselineData,
        "objectCategory": e.target.value,
        "objectName": isBaseline ? baselineObject.name : "",
        "objectVersion": isBaseline ? baselineObject.version : "",
        objectVariant: isBaseline ? baselineObject.objectVariant : "",
        "rcs": isBaseline ? baselineObject.rcs : "",
      }
    } else if (e.target.value === "Potentially Moving Object" || e.target.value === "Miscellaneous") {
      newData = {
        ...baselineData,
        "objectCategory": e.target.value,
        "objectName": isBaseline ? baselineObject.name : "",
        "objectVersion": isBaseline ? baselineObject.version : "",
        "rcs": isBaseline ? baselineObject.rcs : "",
      }
    } else {
      newData = {
        ...baselineData,
        "objectCategory": e.target.value,
        "objectName": "",
        "objectVersion": "",
        "orientationWidth": "",
        "orientationHeight": "",
        "pixelCount": "",
        "dimensionValue": "",
      }
    }
    setBaselineData(newData)
  }

  function handleObjectNameChange(e: any) {
    const latestVersion = getLatestVersion(baselineData.objectCategory, e.target.value)
    let newData;
    if (baselineData.objectCategory === "Potentially Moving Object" || baselineData.objectCategory === "Miscellaneous") {
      newData = {
        ...baselineData,
        objectVariant: '',
        "objectName": e.target.value,
        "objectVersion": latestVersion.latestVersion,
        "orientationWidth": 0,
        "orientationHeight": 0,
        "pixelCount": 0,
        "dimensionValue": 0
      }
    } else if (baselineData.objectCategory === "Sign") {
      newData = {
        ...baselineData,
        objectVariant: '',
        "objectName": e.target.value,
        "objectVersion": latestVersion.latestVersion,
        "orientationWidth": 0,
        "orientationHeight": 0,
        "pixelCount": 0,
        "dimensionValue": 0
      }
    }
    setBaselineData(newData)
  }
  /**
   * Update the object version and rcs based on the selected category, object name and version.
   * This function is triggered when the user selects a new version from the dropdown.
   *
   * @param e - The event object triggered by the version change.
   * @returns  {void} - Updates the baseline data with the new version and RCS properties.
   */
  function handleObjectVersionChange(e: any) {
    const newData = {
      ...baselineData,
      "objectVersion": e.target.value,
    }
    setBaselineData(newData)
  }

  /**
   * Update the object id based on the selected object.
   */
  const groupedObjects = useMemo(() => groupObjectsByTypeNameVersionProfile(props.objectData), [props.objectData])
  const refGroupedObjects = useRef<any>(groupedObjects)
  useEffect(() => {
    refGroupedObjects.current = groupedObjects
  }, [groupedObjects])
  useEffect(() => {
    const updatedBaselineData = addObjectIdToBaselineData(baselineData, refGroupedObjects)
    setBaselineData(updatedBaselineData)
  }, [baselineData.objectName, baselineData.objectVersion, baselineData.objectVariant, groupedObjects]);

  /**
   * Handles changes to the profile data based on the selected object category
   * and dimensions, updating the baseline data accordingly.
   *
   * @param {Event} e - The event object triggered by the profile change.
   * @param {Object} dimensions - An object containing dimensions for the profile.
   * @param {number} [dimensions.width] - The width dimension, if applicable.
   * @param {number} [dimensions.height] - The height dimension, if applicable.
   *
   * @returns {void} - Updates the baseline data with the new profile information.
   *
   * - If the `objectCategory` in `baselineData` is "Potentially Moving Object",
   *   calculates positional sizes using `getPosSizes` and updates the profile
   *   with the target value, positional sizes, and dimensions.
   *
   * - If the `objectCategory` is "Sign" and dimensions are provided or undefined,
   *   uses the passed dimensions or baseline values to update the profile
   *   with the appropriate properties.
   *
   * - Logs a warning if the `objectCategory` is not handled.
   */
  function handleProfileChange(e: any, dimensions: any) {
    let newData;
    if (baselineData.objectCategory === "Potentially Moving Object" || baselineData.objectCategory === 'Miscellaneous') {
      const pmoSizes: any = getPmoSizes(
        baselineData.objectCategory,
        baselineData.objectName,
        baselineData.objectVersion,
        e.target.value,
          objectData
      );
      if (!pmoSizes) {
        return;
      }
      newData = {
        ...baselineData,
        objectVariant: e.target.value,
        orientationWidth: pmoSizes?.orientationWidth,
        orientationHeight: pmoSizes?.orientationHeight,
        pixelCount: "20", // Defaulting to 20
        dimensionValue: calcSelectDimension(
          baselineData.selectDimension,
          pmoSizes?.orientationWidth,
          pmoSizes?.orientationHeight
        ),
      };
    } else if (baselineData.objectCategory === "Sign" && dimensions !== undefined) {
      // Handle the case for "Sign" using the passed dimensions
      newData = {
        ...baselineData,
        objectId: dimensions?.id,
        objectVariant: e.target.value,
        orientationWidth: dimensions?.width || baselineData.orientationWidth,
        orientationHeight: dimensions?.height || baselineData.orientationHeight,
        pixelCount: "20", // Defaulting to 20
        dimensionValue: calcSelectDimension(
          baselineData.selectDimension,
          dimensions?.width,
          dimensions?.height
        ),
      };
    } else {
      console.warn("Unhandled object category:", baselineData.objectCategory);
      return;
    }
    setBaselineData(newData);
  }

  function processListOfVersions(listOfElements: any) {
    let local_map: any = {}
    listOfElements.forEach((item: any) => {
      if (local_map[item.name] !== undefined) {
        local_map[item.name].push(item.version)
      } else {
        local_map[item.name] = []
        local_map[item.name].push(item.version)
      }
    })
    return local_map
  }

  function calcSelectDimension(selectDimension: string, orientationWidth: any, orientationHeight: any) {
    let value;
    if (selectDimension === 'Short') { //choose lower value
      value = orientationHeight < orientationWidth ? orientationHeight : orientationWidth;

    } else { // Long - choose higher value
      value = orientationHeight > orientationWidth ? orientationHeight : orientationWidth;
    }
    return value;
  }

  function handleDimensionChange(e: any) {
    const newData = {
      ...baselineData,
      "selectDimension": e.target.value,
      "dimensionValue": calcSelectDimension(e.target.value, baselineData.orientationWidth, baselineData.orientationHeight)
    }
    setBaselineData(newData)
  }

  function calcVerticalSize(orientationWidth: number, orientationHeight: number, selectDimension: string) {
    if (baselineData.objectCategory == "Custom") {
      const newData = {
        ...baselineData,
        "orientationHeight": orientationHeight
      }
      if (orientationWidth != undefined) {
        newData["dimensionValue"] = calcSelectDimension(selectDimension, orientationWidth, orientationHeight);
      }
      setBaselineData(newData);
    }
  }

  function calcHorizontalSize(orientationWidth: number, orientationHeight: number, selectDimension: string) {
    if (baselineData.objectCategory == "Custom") {
      const newData = {
        ...baselineData,
        "orientationWidth": orientationWidth
      }
      if (orientationHeight != undefined) {
        newData["dimensionValue"] = calcSelectDimension(selectDimension, orientationWidth, orientationHeight);
      }

      setBaselineData(newData);
    }
  }

  function calcPixelCount(pixelCount: any) {
    /*const newData = {
      ...baselineData,
      "pixelsAcrossShortDimension":  e.target.value
    }*/
    setBaselineData({...baselineData, "pixelCount": pixelCount});
  }

  function calcPointsOnTarget(obj: any) {
    setBaselineData({...baselineData, ...obj});
  }

  const createCollapsibleTabs = () => {
    let _componentDataMap: any = {}
    _componentDataMap["Lidar Properties"] = <LidarProperties
      temperatureArray={temperatureArray}
      sensorLidarData={sensorLidarData}
      isDialogOpen={isDialogOpen}
      handleTempChange={handleTempChange}
      baselineData={baselineData}
    />
    _componentDataMap[VAR_COMMON_CONSTANTS.sensorApp.all.SENSOR_TESTING_CONDITIONS_HEADER] = <ObjectProperties
      baselineData={baselineData}
      isDialogOpen={isDialogOpen}

      gmid={gmid}
      configIndex={gmid}

      // rwo
      handleObjectCategoryChange={handleObjectCategoryChange}
      objectData={objectData}
      objectDataMap={objectDataMap}
      handleObjectNameChange={handleObjectNameChange}
      processListOfVersions={processListOfVersions}
      handleObjectVersionChange={handleObjectVersionChange}
      handleProfileChange={handleProfileChange}

      temperatureArray={temperatureArray}
      handleTempChange={handleTempChange}

      newDataList={newTempRelatedDataList}

      // setSelectedObject={setSelectedObject}
      setSelectedPointsFrames={setSelectedPointsFrames}

      setIsPublicConfig={setIsPublicConfig}

      handleDimensionChange={handleDimensionChange}

      calcHorizontalSize={calcHorizontalSize}
      calcVerticalSize={calcVerticalSize}
      calcPixelCount={calcPixelCount}
      calcPointsOnTarget={calcPointsOnTarget}
    />
    _componentDataMap["Sensor Performance"] = <RightPanel 
      isLoadingPlot={isLoadingPlot}
      isError={isError}
      plotData={plotData}
      handleUpdatePlot={handleUpdatePlot}
      baselineData={baselineData}
      handleReset={handleReset}
      handleClickSaveOpen={handleClickSaveOpen}
      openSavePopup={openSavePopup}
      handleClose={handleClose}
      handleSaveConfigName={handleSaveConfigName}
      configName={configName}
      configIndex={props?.configIndex}
      gmid={gmid}
      onOverrideConfiguration={onOverrideConfiguration}
      onSaveConfiguration={onSaveConfiguration}
      onDeleteConfiguration={onDeleteConfiguration}
      fovAndBoresightLidarValues={fovAndBoresightLidarValues}
    />
    setComponentDataMap(_componentDataMap)
  }

  return (
    <ArgosFlexbox vertical style={{overflow: 'auto', height: '100%', width: '100%'}}>
      {/* Make it own component */}
      <ArgosFlexbox style={{alignContent: 'center'}}>
        <BottomButtonsWrapper style={{marginTop: '0px'}}>
          <ArgosButton disabled={isLoadingPlot}
                       onClick={() => handleUpdatePlot(baselineData)}>Update Plot</ArgosButton>
        </BottomButtonsWrapper>
      </ArgosFlexbox>
     <div style={{ flex: 1, overflow: 'auto' }}>
      <ArgosCollapseComponent
        styleProps={{
          outlineOnly: true,
          fontColor: theme.layout.sidebar.backgroundColor,
        }}
        defaultOpen={true}
        data={componentDataMap ?? {}}
      />
    </div>
        
    </ArgosFlexbox>
  )
}
export default Content